import { AlertTriangle, Check, Copy, ExternalLink } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { copyToClipboard } from '~/utils/clipboard';
import { cn } from '~/utils/cn';

import type { PreviewCell } from '../../utils/export-preview-model';

interface PreviewRowDetailProps {
  /** Every column of one sampled product, in header order. */
  cells: PreviewCell[];
  /** Test-id root for E2E; each field derives `${testId}-field-${cell.key}` and friends. */
  testId?: string;
}

/**
 * One sampled product read vertically: every column as a label → full value
 * pair, wrapped rather than truncated.
 *
 * The File grid answers "what will I open in a spreadsheet", which is why it
 * keeps uniform row heights and clips long cells. That makes the values it
 * clips unreadable — a GMC resource name, an encoded typed-text title and a
 * JSON product detail are all longer than any sane column width, and a `title`
 * tooltip is not a way to read data. Expanding a row here answers the other
 * question — "what exactly is in THIS product" — without widening the grid or
 * making every row tall for the sake of a few long ones.
 *
 * The stored value is shown only where the template rewrote it: repeating an
 * identical string under an "exported" heading on all 52 columns is noise, and
 * the point of the pairing is the diff.
 */
export function PreviewRowDetail({
  cells,
  testId = 'export-preview-row-detail',
}: PreviewRowDetailProps) {
  const { t } = useTranslation('exports');
  // Key of the cell whose value was just copied, so the confirmation is scoped
  // to the button that was actually pressed rather than to all of them.
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const resetTimer = useRef<number | undefined>(undefined);

  // The panel unmounts as soon as the row is collapsed, which is well inside
  // the 2s confirmation window.
  useEffect(
    () => () => {
      window.clearTimeout(resetTimer.current);
    },
    [],
  );

  const handleCopy = (cell: PreviewCell) => {
    copyToClipboard(cell.encoded);
    setCopiedKey(cell.key);
    window.clearTimeout(resetTimer.current);
    resetTimer.current = window.setTimeout(() => {
      setCopiedKey(null);
    }, 2000);
  };

  return (
    <dl data-testid={testId} className="divide-y">
      {cells.map((cell) => (
        <div
          key={cell.key}
          data-testid={`${testId}-field-${cell.key}`}
          className="grid gap-x-4 gap-y-1 px-3 py-2 sm:grid-cols-[minmax(9rem,14rem)_1fr]"
        >
          <dt className="flex flex-wrap items-start gap-x-2 gap-y-1 sm:flex-col sm:gap-y-0.5">
            <span className="text-foreground text-xs font-semibold">{cell.label}</span>
            <span className="text-muted-foreground font-mono text-[11px] break-all">
              {cell.key}
            </span>
            <div className="flex flex-wrap items-center gap-1">
              {cell.identifier && (
                <Badge variant="outline" className="pointer-events-none text-[10px] font-normal">
                  {t('preview.changes.identifier')}
                </Badge>
              )}
              {cell.transformationLabel && (
                <Badge variant="outline" className="pointer-events-none text-[10px] font-normal">
                  {cell.transformationLabel}
                </Badge>
              )}
            </div>
          </dt>

          <dd className="min-w-0">
            <div className="flex items-start gap-1">
              <span
                data-testid={`${testId}-value-${cell.key}`}
                className={cn(
                  'text-foreground min-w-0 flex-1 font-mono text-xs break-all whitespace-pre-wrap',
                  !cell.encoded && 'text-muted-foreground',
                )}
              >
                {cell.encoded || '—'}
              </span>
              {/* Copy is per value, not per row: the reason to open this panel
                  is usually to lift one id or one encoded cell out of it. */}
              {cell.encoded && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0 cursor-pointer"
                  data-testid={`${testId}-copy-button-${cell.key}`}
                  aria-label={
                    copiedKey === cell.key
                      ? t('preview.copied')
                      : t('preview.detail.copyValue', { column: cell.label })
                  }
                  onClick={() => {
                    handleCopy(cell);
                  }}
                >
                  {copiedKey === cell.key ? (
                    <Check aria-hidden="true" className="text-success size-3" />
                  ) : (
                    <Copy aria-hidden="true" className="size-3" />
                  )}
                </Button>
              )}
            </div>

            {/* Only where the template actually rewrote the value — see above. */}
            {cell.changed && (
              <p
                data-testid={`${testId}-stored-${cell.key}`}
                className="text-muted-foreground mt-1 font-mono text-[11px] break-all whitespace-pre-wrap"
              >
                <span className="mr-1 font-sans font-semibold">{t('preview.stored')}</span>
                {cell.raw || '—'}
              </p>
            )}

            {/* Advisory, exactly as in the Changes view: the export neither
                truncates nor rejects on a spec limit. */}
            {cell.specWarning && (
              <p
                data-testid={`${testId}-spec-warning-${cell.key}`}
                className="text-warning mt-1 flex flex-wrap items-center gap-1 text-[11px]"
              >
                <AlertTriangle aria-hidden="true" className="size-3 shrink-0" />
                {cell.specWarning.kind === 'length'
                  ? t('preview.spec.length', {
                      actual: cell.specWarning.actual,
                      limit: cell.specWarning.limit,
                    })
                  : t('preview.spec.repetitions', {
                      actual: cell.specWarning.actual,
                      limit: cell.specWarning.limit,
                    })}
                {cell.specWarning.docUrl && (
                  <Button
                    asChild
                    variant="link"
                    size="sm"
                    className="text-warning h-auto cursor-pointer p-0 text-[11px] underline"
                  >
                    <a
                      href={cell.specWarning.docUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      data-testid={`${testId}-docs-link-${cell.key}`}
                    >
                      {t('preview.spec.docs')}
                      <ExternalLink aria-hidden="true" className="ml-0.5 size-3" />
                    </a>
                  </Button>
                )}
              </p>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}
